import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  Alert,
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  InputAdornment,
  ListItemButton,
  Radio,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined'
import SharedDialog from '../../../shared/components/Dialog'
import SharedButton from '../../../shared/components/Button'
import StatusChip from '../../../shared/components/StatusChip'
import { getDialogTextFieldSx } from '../../../shared/styles/formStyles'
import { statusConfig } from '../../../shared/utilities/statusConfig'
import { useThemeMode } from '../../../shared/hooks'
import { getImpersonationMembers, startImpersonation } from '../../../logic/apis/admin'
import {
  ImpersonationMember,
  MEMBERS_PAGE_LIMIT,
  POPUP_BLOCKED_MESSAGE,
  REASON_MAX,
  START_TIMEOUT_MS,
  blockedReasonLabel,
  isReasonValid,
  normalizeMembersPage,
  normalizeStart,
  startFailureMessage,
  validateReason,
  withTimeout,
} from '../../../logic/interfaces/adminImpersonation'

interface LogInAsDialogProps {
  open: boolean
  onClose: () => void
  companyId: string
  companyName: string
}

interface MemberList {
  items: ImpersonationMember[]
  nextCursor: string | null
  hasNextPage: boolean
  phase: 'loading' | 'loadingMore' | 'ready' | 'failed'
}

const EMPTY_LIST: MemberList = { items: [], nextCursor: null, hasNextPage: false, phase: 'loading' }

const FieldLabel = ({ children }: { children: string }) => (
  <Typography component="label" sx={{ display: 'block', fontSize: '13px', fontWeight: 600, mb: 0.75 }}>
    {children}{' '}
    <Box component="span" sx={{ color: 'error.main' }}>
      *
    </Box>
  </Typography>
)

const LogInAsDialog = ({ open, onClose, companyId, companyName }: LogInAsDialogProps) => {
  const { isDark } = useThemeMode()
  const fieldSx = getDialogTextFieldSx(isDark)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [list, setList] = useState<MemberList>(EMPTY_LIST)
  const [selected, setSelected] = useState<string | null>(null)
  const [ownerId, setOwnerId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [snack, setSnack] = useState<string | null>(null)
  const preselected = useRef(false)
  const ownerScrolled = useRef(false)
  const listRef = useRef<HTMLDivElement>(null)
  const requestSeq = useRef(0)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<{ reason: string }>({ defaultValues: { reason: '' }, mode: 'onBlur' })
  const reason = watch('reason')

  const loadPage = useCallback(
    (cursor: string | null, term: string) => {
      const seq = ++requestSeq.current
      void (async () => {
        try {
          const res = await getImpersonationMembers(companyId, {
            limit: MEMBERS_PAGE_LIMIT,
            ...(cursor ? { cursor } : {}),
            ...(term ? { search: term } : {}),
          })
          if (seq !== requestSeq.current) return
          const page = normalizeMembersPage(res)
          if (!page) {
            setList((prev) => ({ ...prev, phase: 'failed' }))
            return
          }
          // Preselect before the list turns ready: the scroll effect reads ownerId on that render.
          if (!preselected.current) {
            preselected.current = true
            const owner = page.defaultMemberId
            const ownerRow = page.items.find((m) => m.memberId === owner)
            if (owner && !ownerRow?.blockedReason) {
              setSelected(owner)
              setOwnerId(owner)
            }
          }
          setList((prev) => ({
            items: cursor ? [...prev.items, ...page.items] : page.items,
            nextCursor: page.nextCursor,
            hasNextPage: page.hasNextPage,
            phase: 'ready',
          }))
        } catch {
          if (seq === requestSeq.current) setList((prev) => ({ ...prev, phase: 'failed' }))
        }
      })()
    },
    [companyId]
  )

  // Reset on every open: a fresh member list, the Owner preselected again and an empty reason.
  useEffect(() => {
    if (!open) return
    preselected.current = false
    ownerScrolled.current = false
    setSearch('')
    setQuery('')
    setSelected(null)
    setOwnerId(null)
    setSnack(null)
    reset({ reason: '' })
  }, [open, reset])

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (!open) return
    setList(EMPTY_LIST)
    loadPage(null, query)
  }, [open, query, loadPage])

  // Once per open, on the first loaded list: bring the preselected Owner row into view inside the list only
  // (block 'nearest'; the dialog itself never scrolls). The row keeps its place in name order.
  useEffect(() => {
    const container = listRef.current
    if (!open || ownerScrolled.current || list.phase !== 'ready' || !container) return
    ownerScrolled.current = true
    if (!ownerId) return
    const row = container.querySelector<HTMLElement>(`[data-member-id="${CSS.escape(ownerId)}"]`)
    if (!row) return
    const box = container.getBoundingClientRect()
    const r = row.getBoundingClientRect()
    if (r.top < box.top) container.scrollTop -= box.top - r.top
    else if (r.bottom > box.bottom) container.scrollTop += r.bottom - box.bottom
  }, [open, list.phase, ownerId])

  const canSubmit = !!selected && isReasonValid(reason) && !submitting
  // The Owner stays selected even when their row is not loaded (past page 1, or filtered out by a search).
  const ownerHidden =
    !!ownerId && selected === ownerId && list.phase !== 'loading' && !list.items.some((m) => m.memberId === ownerId)

  const onSubmit = handleSubmit(async (values) => {
    if (!selected) return
    // Opened synchronously inside the click so popup blockers allow it; pointed at the link once the POST resolves.
    const tab = window.open('about:blank', '_blank')
    if (!tab) {
      setSnack(POPUP_BLOCKED_MESSAGE)
      return
    }
    tab.opener = null
    setSubmitting(true)
    setSnack(null)
    try {
      const started = normalizeStart(
        await withTimeout(
          startImpersonation({ companyId, memberId: selected, reason: values.reason.trim() }),
          START_TIMEOUT_MS
        )
      )
      if (!started) throw new Error('unreadable start response')
      tab.location.href = started.url
      onClose()
    } catch (err) {
      tab.close()
      setSnack(startFailureMessage(err))
    } finally {
      setSubmitting(false)
    }
  })

  const close = () => {
    if (!submitting) onClose()
  }

  const counter = `${reason.length} / ${REASON_MAX}`

  return (
    <>
      <SharedDialog
        variant="form"
        open={open}
        onClose={close}
        title="Log in as a member"
        subtitle={companyName}
        actions={null}
        maxWidth={600}
      >
        <Box component="form" onSubmit={(e: FormEvent<HTMLFormElement>) => void onSubmit(e)} noValidate>
          <DialogContent sx={{ pt: 2, pb: 2 }}>
            <FieldLabel>Member</FieldLabel>
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              // Enter in the search box must never submit the form and open a session.
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault()
              }}
              placeholder="Search by name or email"
              inputProps={{ 'aria-label': 'Search members by name or email' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
            {ownerHidden && (
              <Typography sx={{ mt: 0.75, mx: 0.25, fontSize: '12px', color: 'text.secondary' }}>
                Owner preselected - search to find them
              </Typography>
            )}
            <Box
              ref={listRef}
              role="radiogroup"
              aria-label="Member"
              sx={{
                mt: 1.25,
                border: 1,
                borderColor: 'divider',
                borderRadius: '10px',
                overflow: 'hidden',
                maxHeight: 300,
                overflowY: 'auto',
              }}
            >
              {list.phase === 'loading' && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={22} />
                </Box>
              )}
              {list.phase === 'failed' && !list.items.length && (
                <Box sx={{ px: 2, py: 2.5, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 1 }}>
                    Could not load the members.
                  </Typography>
                  <SharedButton
                    label="Try again"
                    btnVariant="secondary"
                    size="small"
                    onClick={() => loadPage(null, query)}
                  />
                </Box>
              )}
              {list.phase !== 'loading' && list.phase !== 'failed' && !list.items.length && (
                <Typography sx={{ px: 2, py: 2.5, fontSize: '13px', color: 'text.secondary', textAlign: 'center' }}>
                  {query ? 'No active members match this search.' : 'This company has no active members.'}
                </Typography>
              )}
              {list.phase !== 'loading' &&
                list.items.map((m) => {
                  const isSel = m.memberId === selected
                  const blocked = !!m.blockedReason
                  return (
                    <ListItemButton
                      key={m.memberId}
                      data-member-id={m.memberId}
                      role="radio"
                      aria-checked={isSel}
                      aria-disabled={blocked || undefined}
                      disabled={blocked || submitting}
                      onClick={() => setSelected(m.memberId)}
                      sx={{
                        gap: 1.5,
                        px: 1.75,
                        py: 1.25,
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&:last-of-type': { borderBottom: 0 },
                        bgcolor: isSel ? (theme) => alpha(theme.palette.info.main, 0.06) : undefined,
                        '&.Mui-disabled': { opacity: 1, bgcolor: 'action.hover' },
                      }}
                    >
                      <Radio
                        checked={isSel}
                        tabIndex={-1}
                        size="small"
                        disableRipple
                        inputProps={{ 'aria-hidden': true }}
                        sx={{ p: 0, opacity: blocked ? 0.45 : 1 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0, opacity: blocked ? 0.45 : 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography noWrap sx={{ fontSize: '14px', fontWeight: 600 }}>
                            {m.name || m.email}
                          </Typography>
                          {m.isOwner && <StatusChip {...statusConfig.trialing} label="Owner" />}
                        </Box>
                        <Typography noWrap sx={{ fontSize: '12px', color: 'text.secondary' }}>
                          {m.email}
                        </Typography>
                      </Box>
                      {m.blockedReason ? (
                        <Typography sx={{ fontSize: '12px', color: 'warning.dark', textAlign: 'right', maxWidth: 190 }}>
                          {blockedReasonLabel(m.blockedReason)}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', textAlign: 'right' }}>
                          {m.position ?? ''}
                        </Typography>
                      )}
                    </ListItemButton>
                  )
                })}
              {list.hasNextPage && list.phase !== 'loading' && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <SharedButton
                    label="Load more"
                    btnVariant="text"
                    size="small"
                    loading={list.phase === 'loadingMore'}
                    onClick={() => {
                      setList((prev) => ({ ...prev, phase: 'loadingMore' }))
                      loadPage(list.nextCursor, query)
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 2.25 }}>
              <FieldLabel>Reason</FieldLabel>
              <Controller
                name="reason"
                control={control}
                rules={{ validate: validateReason }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    disabled={submitting}
                    placeholder="Why are you opening this account? Include the ticket number."
                    error={!!errors.reason}
                    inputProps={{ 'aria-label': 'Reason', maxLength: REASON_MAX + 50 }}
                    sx={fieldSx}
                  />
                )}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  mt: 0.625,
                  mx: 0.25,
                  fontSize: '12px',
                  color: errors.reason ? 'error.main' : 'text.secondary',
                }}
              >
                <span>{errors.reason?.message ?? 'Required. 10 to 500 characters. Saved to the audit log.'}</span>
                <span>{counter}</span>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 2,
                display: 'flex',
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                borderRadius: '10px',
                bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
                color: 'info.main',
                fontSize: '13px',
                lineHeight: 1.45,
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18, mt: '1px' }} />
              <span>
                Opens read-only in a new tab. Use a private window if you are signed in to NHR in this browser.
              </span>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.75, borderTop: 1, borderColor: 'divider' }}>
            <SharedButton label="Cancel" btnVariant="secondary" disabled={submitting} onClick={close} />
            <SharedButton
              label="Open session"
              type="submit"
              disabled={!canSubmit}
              loading={submitting}
              loadingText="Opening..."
              startIcon={<LoginOutlinedIcon />}
            />
          </DialogActions>
        </Box>
      </SharedDialog>
      <Snackbar
        open={!!snack}
        autoHideDuration={8000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setSnack(null)} role="alert">
          {snack}
        </Alert>
      </Snackbar>
    </>
  )
}

export default LogInAsDialog
